<template>
  <li class="nav-item dropdown">
    <dropdown
      placement="bottom-end"
      noPadding
      @show="show"
      @applyHide="hide()"
      ref="notificationMenu"
    >
      <template #button>
        <a href="#" @click.prevent class="nav-link">
          <i class="al-icon-notifications fade-alert mr-sm-2" style="display:inline-block" :class="{ 'clap-animating fade-alert-in': isAnimating }" />
          <span class="t-500 d-none d-lg-inline-block" id="notification-header-dropdown">Notifications</span>
          <i class="fa fa-circle t-xxxs t-yellow fa-info-absolute fa-info-absolute--navbar" v-if="unreadCount > 0" />
        </a>
        <flash />
      </template>
      <div class="dropdown-menu-custom--notification box-shadow pt-0 t-regular">
        <div class="wrapper-header clearfix t-sm mb-2">
          <p class="t-lr mb-0 t-600 pull-left">
            Notifications
          </p>
          <a v-if="notifications.results.length > 0" href="#" @click.prevent="readAll()" class="pull-right">Mark all as read</a>
        </div>
        <div class="wrapper-outer">
          <div ref="notificationWrapper" class="wrapper-inner">
            <template v-if="notifications.results.length > 0">
              <div class="dropdown-items">
                <div v-for="(notification, index) in notifications.results"
                  :key="index"
                  class="dropdown-item"
                  :class="{'mb-0': index === notifications.results.length - 1}"
                  @click="openNotification(notification)"
                >
                  <div class="dropdown-item--left avatar-header" >
                    <notification-image :notification="notification.data" />
                  </div>
                  <div class="dropdown-item--right t-regular" :class="{'bg-highlight': !notification.viewed_at}">
                    <span class="message">
                      <span class="t-600">{{ notification.data.message_prefix }} </span>
                      <span v-html="notification.data.message" />
                      <span class="t-600"> {{ notification.data.message_suffix }}</span>
                      <copy-icon v-if="notification.data.copy_text" :text="notification.data.copy_text" />
                    </span>
                    <span class="d-block t-secondary">{{ notification.created_at | fromNow }}</span>
                  </div>
                </div>
              </div>
            </template>

            <spinner v-if="!isLoaded" :containerClass="`width--50px min-height--65px`" />

            <infinite-loading
              @infinite="infiniteHandler"
              :distance="10"
              :identifier="infiniteId"
              ref="infiniteLoading"
              class="mt-2"
            >
              <span slot="spinner"></span>

              <span slot="no-results">
                <div v-if="notifications.results.length === 0" class="pt-2 pb-3">You have no notifications...</div>
                <div v-if="notifications.results.length > 0" class="pt-2 t-sm">No more notifications to show...</div>
              </span>

              <span slot="no-more">
                <div class="pt-2 t-sm">No more notifications to show...</div>
              </span>
            </infinite-loading>
          </div>
        </div>
      </div>
    </dropdown>
  </li>
</template>

<script>
/**
 * The Notifications menu
 */
import Spinner from '@/components/shared/Spinner'
import NotificationImage from '@/components/shared/NotificationImage'
import InfiniteLoading from 'vue-infinite-loading'
import Dropdown from '@/components/shared/form/Dropdown'
import Flash from '@/components/shared/Flash'
import CopyIcon from '@/components/shared/CopyIcon'

import NotificationService from '@/services/notifications'

export default {
  name: 'notificationDropdown',
  components: {
    Spinner,
    NotificationImage,
    InfiniteLoading,
    Dropdown,
    Flash,
    CopyIcon
  },
  data () {
    return {
      isAnimating: false,
      isActive: false, // Is the menu shown?
      isLoaded: false, // Is the menu busy loading (more) messages?
      isCompleted: false, // Have all messages been loaded? (i.e. the user scrolled to the bottom of the list)
      page: 0,
      perPage: 12,
      notifications: {
        page_count: 0,
        total_count: 0,
        results: []
      },
      error: null,
      infiniteId: +new Date(), // An identifier for the infinite-loading component,
      unreadCount: 0
    }
  },
  created () {
    document.addEventListener('visibilitychange', (e) => {
      if (document.visibilityState === 'visible') {
        this.fetchUnreadCountFromRds()
      }
    })

    this.unreadCount = this.fetchUnreadCountFromRds()

    this.$events.$on('notify-bell', (notification) => {
      if (this.isActive) {
        // If the menu is showing, hide it. (because a Flash / notification preview is now showing in the same location)
        this.$refs.notificationMenu.closePopover()
        this.hide()
      }

      // If silent, it's marked-as-read in advance
      const silent = Boolean(notification.silent)
      if (!silent) {
        this.unreadCount += 1
      }

      this.isAnimating = true
      setTimeout(() => { this.isAnimating = false }, 1600)

      let data = this.$ls.getAccountData()
      data.isNotificationOpened = silent
      this.$ls.setAccountData(data)
    })

    this.$events.$on('un-notify-bell', (id) => {
      this.updateNotification(id)
      this.unreadCount -= 1
    })
  },
  methods: {
    isBottom (el) {
      return el.getBoundingClientRect().bottom - 200 <= 400
    },
    show () {
      this.isActive = true
      this.infiniteId += 1

      let data = this.$ls.getAccountData()
      data.isNotificationOpened = true
      this.$ls.setAccountData(data)
    },
    hide () {
      this.reset()
    },
    infiniteHandler ($state) {
      this.page = this.page + 1
      this.isLoaded = false

      NotificationService.get(this.page, this.perPage).then(notifications => {
        if (this.notifications.results) {
          this.notifications.page_count = notifications.page_count
          this.notifications.total_count = notifications.total_count
          this.notifications.results.push(...notifications.results)
        } else {
          this.notifications = notifications
        }

        this.isLoaded = true

        // Set on completed
        if (this.notifications.total_count > this.page * this.perPage) {
          if ($state) $state.loaded()
        } else {
          if ($state) $state.complete()
          this.isCompleted = true
        }
      }).catch((err) => {
        this.error = this.handleError(err)
        this.isLoaded = !this.isEmpty(this.error)
      })
    },
    openNotification (notification) {
      const link = notification.data.link
      if (link) {
        if (link.startsWith('https:')) {
          window.open(link, '_blank')
        } else if (link.startsWith('mailto:')) {
          window.location.href = link
        } else {
          this.$router.push({ path: `${link}` })
        }
      }
      this.hide()

      // update notification on viewed
      this.updateNotification(notification.id)

      if (this.unreadCount > 0) {
        this.unreadCount -= 1
      }
    },
    readAll () {
      NotificationService.update([])
      this.notifications.results.map((r) => { r.viewed_at = new Date() })
      this.unreadCount = 0
      this.$set(this.$currentUser, 'notifications', 0)
      this.$ls.setUser(this.$currentUser)
    },
    updateNotification (id) {
      let ids = []
      ids.push(id)
      NotificationService.update(ids)
    },
    fetchUnreadCountFromRds () {
      NotificationService.unread().then(obj => {
        this.unreadCount = obj.unread
      })
    },
    reset () {
      this.page = 0
      this.isLoaded = false
      this.isCompleted = false
      this.isActive = false
      this.notifications = this.$options.data().notifications
    }
  },
  destroyed () {
    this.$events.$off('notify-bell')
    this.$events.$off('un-notify-bell')
  }
}
</script>
